import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LogOut, ChevronRight, Menu, X } from "lucide-react";
import { useAuth } from "../../context/AppContext";
import { NotificationsBell } from "./NotificationsBell";
import { ThemeToggle } from "./ThemeToggle";
import { Logo } from "./Logo";
import { BottomNav } from "./BottomNav";
import { bottomNavFor } from "./navConfig";

const NavItems = ({ nav, onNavigate }) => (
  <nav className="px-3 flex-1 overflow-y-auto space-y-0.5">
    {nav.map(item => (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.end}
        onClick={onNavigate}
        data-testid={`nav-${item.key}`}
        className={({ isActive }) =>
          `group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors duration-200 border ${
            isActive ? "bg-blue-500/10 border-blue-500/25" : "border-transparent hover:bg-white/5"
          }`
        }
        style={({ isActive }) => ({ color: isActive ? "var(--text)" : "var(--text-muted)" })}
      >
        <item.icon size={15} className="opacity-90" />
        <span>{item.label}</span>
        {item.badge && (
          <span className="ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-500 border border-blue-500/25">
            {item.badge}
          </span>
        )}
      </NavLink>
    ))}
  </nav>
);

export const DashboardShell = ({ nav, title, subtitle, children, headerRight }) => {
  const navigate = useNavigate();
  const { role, user, logout: doLogout } = useAuth();
  const roleLabel = role === "student" ? "Student" : role === "company" ? "Company" : "Admin";
  const [drawerOpen, setDrawerOpen] = useState(false);
  const bottomNav = bottomNavFor(role);

  const logout = async () => { await doLogout(); navigate("/"); };
  const closeDrawer = () => setDrawerOpen(false);

  const AsideInner = (
    <>
      <div className="px-5 py-6 flex items-center justify-between">
        <Logo />
        <button
          data-testid="drawer-close-btn"
          className="lg:hidden p-1 rounded-md hover:bg-white/5"
          onClick={closeDrawer}
          style={{ color: "var(--text-muted)" }}
        >
          <X size={18}/>
        </button>
      </div>
      <div className="px-5 -mt-2 mb-2 eyebrow" style={{ color: "var(--brand)" }}>{roleLabel} console</div>
      <NavItems nav={nav} onNavigate={closeDrawer}/>
      <div className="p-4 border-t" style={{ borderColor: "var(--border)" }}>
        <button
          data-testid="sidebar-logout-btn"
          onClick={logout}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-white/5 transition-colors duration-200"
          style={{ color: "var(--text-muted)" }}
        >
          <LogOut size={14}/>
          <span>Sign out</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg)" }}>
      {/* Desktop sidebar */}
      <aside
        className="w-64 shrink-0 border-r flex-col hidden lg:flex sticky top-0 h-screen"
        style={{ borderColor: "var(--border)", background: "var(--surface)" }}
      >
        {AsideInner}
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <>
          <div
            data-testid="drawer-backdrop"
            className="lg:hidden fixed inset-0 z-40 bg-black/50"
            onClick={closeDrawer}
          />
          <aside
            data-testid="mobile-drawer"
            className="lg:hidden fixed inset-y-0 left-0 z-50 w-72 border-r flex flex-col"
            style={{ borderColor: "var(--border)", background: "var(--surface)" }}
          >
            {AsideInner}
          </aside>
        </>
      )}

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="sticky top-0 z-30 px-3 md:px-6 pt-3">
        <header
          data-testid="dashboard-capsule-nav"
          className="capsule-nav px-3 md:px-4 py-2 flex items-center gap-2 md:gap-3"
        >
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden p-1.5 rounded-full hover:bg-white/5"
            onClick={() => setDrawerOpen(true)}
            style={{ color: "var(--text)" }}
          >
            <Menu size={18}/>
          </button>
          <div className="lg:hidden">
            <Logo size="sm"/>
          </div>
          <div className="hidden md:flex items-center gap-2 text-xs" style={{ color: "var(--text-dim)" }}>
            <span>{roleLabel}</span>
            <ChevronRight size={12}/>
            <span style={{ color: "var(--text)" }}>{title}</span>
          </div>
          <div className="ml-auto flex items-center gap-2 md:gap-3">
            <NotificationsBell/>
            <ThemeToggle/>
            {headerRight}
            <div
              className="hidden sm:flex items-center gap-2 px-2 py-1 rounded-full border"
              style={{ borderColor: "var(--border-strong)", background: "var(--surface)" }}
              data-testid="header-user-chip"
            >
              {user?.picture
                ? <img alt="" src={user.picture} className="w-6 h-6 rounded-full object-cover"/>
                : <div className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-500 flex items-center justify-center text-[10px] font-mono">{(user?.name || user?.email || "?").slice(0,1).toUpperCase()}</div>}
              <span className="hidden md:inline text-xs truncate max-w-[120px]" style={{ color: "var(--text-muted)" }}>{user?.name || user?.email}</span>
            </div>
          </div>
        </header>
        </div>

        <div className="px-4 sm:px-6 py-6 md:py-8 max-w-[1400px] pb-24 lg:pb-8">
          <div className="mb-6 md:mb-8">
            <h1 data-testid="page-title" className="font-display text-2xl sm:text-3xl md:text-4xl font-medium" style={{ color: "var(--text)" }}>{title}</h1>
            {subtitle && <p className="text-sm mt-1.5" style={{ color: "var(--text-dim)" }}>{subtitle}</p>}
          </div>
          {children}
        </div>
      </main>

      {/* Mobile bottom nav */}
      <BottomNav items={bottomNav}/>
    </div>
  );
};

export default DashboardShell;
